import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FiList } from 'react-icons/fi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Header from '../components/Header';
import BreadcrumbBack from '../components/BreadcrumbBack';
import CaptionPanel from '../components/clip-editor/CaptionPanel';
import KineticCaptionOverlay from '../components/clip-editor/KineticCaptionOverlay';
import KineticPresetPreview from '../components/clip-editor/KineticPresetPreview';
import KineticVideoUpload from '../components/clip-editor/KineticVideoUpload';
import RendersPanel, { DerivedShorts, RenderEdit, RenderJob, RenderOutput } from '../components/clip-editor/RendersPanel';
import GenerateShortsModal, { GenerateShortsTarget } from '../components/clip-editor/GenerateShortsModal';
import RenderConfirmModal from '../components/clip-editor/RenderConfirmModal';
import FullPagePaymentWall, { PaymentWallConfirmation, PaymentWallPreflight } from '../components/FullPagePaymentWall';
import { runPayNowFlow } from '../utils/payNowFlow';

const StyleIcon = FiList as unknown as React.FC<{ 'aria-hidden'?: boolean }>;

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

interface Project {
  id: string;
  name: string;
}

interface CaptionWord {
  text: string;
  start: number;
  end: number;
}

interface Caption {
  id: string;
  text: string;
  start_time: number;
  end_time: number;
  style: Record<string, any>;
  words?: CaptionWord[];
}

interface Preset {
  id: string;
  label: string;
  premium: boolean;
}

interface LatestJob {
  id: string;
  status: string;
  stage: string;
  progress: number;
}

interface Workspace {
  id: string;
  name: string;
  status: string;
  videoId: string | null;
  latestJob: LatestJob | null;
  kineticCaptionConfig: Partial<KineticCaptionConfig> | null;
}

type AspectRatio = '9:16' | '16:9' | '1:1' | '4:5';

interface KineticCaptionConfig {
  preset: string;
  fontFamily: string;
  /** Caption size as % of frame height — resolution-independent, converted to ASS Fontsize at render. */
  fontSizePct: number;
  /** @deprecated legacy absolute size; only read when restoring an older saved config. */
  fontSize?: number;
  bold: boolean;
  italic: boolean;
  underline: boolean;
  textColor: string;
  highlightColor: string;
  aspectRatio: AspectRatio;
}

interface PaymentWallState {
  preflight: PaymentWallPreflight;
  action: 'render' | 'retranscribe';
  config?: KineticCaptionConfig;
}

const PRESETS: Preset[] = [
  { id: 'word-pop', label: 'Word Pop', premium: false },
  { id: 'karaoke-box', label: 'Karaoke Box', premium: false },
  { id: 'karaoke-color', label: 'Karaoke Color', premium: false },
  { id: 'karaoke-scale', label: 'Karaoke Scale', premium: true },
  { id: 'fill-sweep', label: 'Fill Sweep', premium: true },
  { id: 'bounce-in', label: 'Bounce In', premium: true },
  { id: 'typewriter', label: 'Typewriter', premium: true },
  { id: 'underline-marker', label: 'Underline Marker', premium: false },
  { id: 'build-up', label: 'Build Up', premium: false },
];

const ASPECT_RATIOS: { value: AspectRatio; label: string }[] = [
  { value: '9:16', label: '9:16' },
  { value: '16:9', label: '16:9' },
  { value: '1:1', label: '1:1' },
  { value: '4:5', label: '4:5' },
];

const CAPTION_FONTS = [
  'Inter', 'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Poppins',
  'Oswald', 'Bebas Neue', 'Anton', 'Arial', 'Georgia', 'Impact',
];

// Square fit region: keeps the 270x480 frame fixed while letting BOTH dimensions of the
// video change with the ratio (a 9:16-shaped region would pin width at 270 for every ratio).
const VIDEO_FRAME_MAX = { width: 270, height: 270 };

// Caption size is a % of frame height so it renders consistently across aspect ratios.
// ~6% of a 1920-tall frame is ~115px, the usual range for burned-in captions.
const FONT_PCT_MIN = 2;
const FONT_PCT_MAX = 15;
const FONT_PCT_DEFAULT = 6;
const FONT_PCT_STEP = 0.5;

// Legacy configs stored an absolute ASS Fontsize authored against a 1080x1920 canvas.
const legacyPxToPct = (px: number | undefined) =>
  typeof px === 'number' && px > 0 ? Math.min(FONT_PCT_MAX, Math.max(FONT_PCT_MIN, (px / 1920) * 100)) : FONT_PCT_DEFAULT;

const formatClock = (t: number) => {
  if (!isFinite(t) || t < 0) t = 0;
  const m = Math.floor(t / 60);
  const s = Math.floor(t % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

// Same box-fit math the browser applies to the <video> via CSS aspect-ratio + object-fit: contain,
// so the caption overlay tracks the video's actual rendered area (and what ffmpeg will actually burn onto).
const fitFrameSize = (ratio: AspectRatio, bounds = VIDEO_FRAME_MAX): { width: number; height: number } => {
  const [rw, rh] = ratio.split(':').map(Number);
  const scale = Math.min(bounds.width / rw, bounds.height / rh);
  return { width: Math.round(rw * scale), height: Math.round(rh * scale) };
};

const DEFAULT_CAPTIONS: Caption[] = [
  { id: 'c1', text: 'So', start_time: 0, end_time: 0.6, style: {} },
  { id: 'c2', text: 'I', start_time: 0.6, end_time: 0.9, style: {} },
  { id: 'c3', text: 'was', start_time: 0.9, end_time: 1.3, style: {} },
  { id: 'c4', text: 'like this the whole time.', start_time: 1.3, end_time: 3.1, style: {} },
];

const isTerminalJobStatus = (status: string) => ['done', 'completed', 'failed'].includes(status);

const presetLabel = (preset: string | undefined) => {
  if (!preset) return 'Unknown preset';
  return preset.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

function isPaymentRequiredPayload(payload: unknown): payload is PaymentWallPreflight {
  if (!payload || typeof payload !== 'object') return false;
  const obj = payload as Record<string, unknown>;
  return obj.status === 'payment_required'
    && typeof obj.featureKey === 'string'
    && typeof obj.units === 'number'
    && typeof obj.includedUnitsRemaining === 'number'
    && typeof obj.paygOption === 'object'
    && obj.paygOption !== null
    && typeof (obj.paygOption as Record<string, unknown>).unitPrice === 'number'
    && typeof (obj.paygOption as Record<string, unknown>).estimatedAmount === 'number'
    && typeof obj.plansOption === 'object'
    && obj.plansOption !== null
    && Array.isArray((obj.plansOption as Record<string, unknown>).recommendedPlanIds);
}

const KineticCaption: React.FC = () => {
  const { id: projectId, workspaceId: routeWorkspaceId } = useParams<{ id: string; workspaceId?: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [editingName, setEditingName] = useState(!routeWorkspaceId);
  const [draftName, setDraftName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<'style' | 'renders'>('style');
  const [shortsTarget, setShortsTarget] = useState<GenerateShortsTarget | null>(null);
  const [selectedPreset, setSelectedPreset] = useState('karaoke-box');
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [captions, setCaptions] = useState<Caption[]>(DEFAULT_CAPTIONS);
  const [captionsLoaded, setCaptionsLoaded] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [frameBounds, setFrameBounds] = useState(VIDEO_FRAME_MAX);
  const videoRef = useRef<HTMLVideoElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const appliedInitialConfigRef = useRef(false);
  const [pendingNewJobId, setPendingNewJobId] = useState<string | null>(null);

  const [fontFamily, setFontFamily] = useState('Poppins');
  const [fontSizePct, setFontSizePct] = useState(FONT_PCT_DEFAULT);
  const [bold, setBold] = useState(true);
  const [italic, setItalic] = useState(false);
  const [underline, setUnderline] = useState(false);
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [highlightColor, setHighlightColor] = useState('#00D4AA');
  const [aspectRatio, setAspectRatio] = useState<AspectRatio>('9:16');

  // Below 900px the style column is a right-hand overlay drawer (see responsive.css).
  const [styleOpen, setStyleOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [jobs, setJobs] = useState<RenderJob[]>([]);
  const [previewJob, setPreviewJob] = useState<RenderJob | null>(null);
  const [refreshSignal, setRefreshSignal] = useState(0);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [pendingConfig, setPendingConfig] = useState<KineticCaptionConfig | null>(null);
  const [showRetranscribeModal, setShowRetranscribeModal] = useState(false);
  const [retranscribing, setRetranscribing] = useState(false);

  const [paymentWall, setPaymentWall] = useState<PaymentWallState | null>(null);
  const [processingPaymentWall, setProcessingPaymentWall] = useState(false);
  const pendingChargeAuthorizationIdRef = useRef<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!projectId) return;
    fetch(`${API_BASE}/api/projects/${projectId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => setProject(data))
      .catch(() => setProject(null));
  }, [projectId]);

  const fetchWorkspace = useCallback(async (id: string) => {
    const res = await fetch(`${API_BASE}/api/caption-workspaces/${id}`, { credentials: 'include' });
    if (!res.ok) return null;
    const data = await res.json();
    const ws: Workspace = {
      id: data.id, name: data.name, status: data.status, videoId: data.videoId,
      latestJob: data.latestJob, kineticCaptionConfig: data.kineticCaptionConfig || null,
    };
    setWorkspace(ws);
    return ws;
  }, []);

  useEffect(() => {
    if (!routeWorkspaceId) { setLoading(false); return; }
    fetchWorkspace(routeWorkspaceId).finally(() => setLoading(false));
  }, [routeWorkspaceId, fetchWorkspace]);

  // Measure the frame so the video (and the caption overlay tracking it) fill the panel at any
  // window size. Pixel math rather than CSS max-* so both boxes are guaranteed identical.
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const measure = () => setFrameBounds({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [workspace?.videoId, captionsLoaded, previewJob, videoUrl]);

  // Drive currentTime off rAF while playing, so per-word/per-character animations (typewriter, fill-sweep)
  // update smoothly instead of at the browser's throttled `timeupdate` rate (~4/sec).
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    let raf = 0;
    const tick = () => {
      setCurrentTime(video.currentTime);
      raf = requestAnimationFrame(tick);
    };
    const handlePlay = () => { setPlaying(true); raf = requestAnimationFrame(tick); };
    const handlePause = () => { setPlaying(false); cancelAnimationFrame(raf); setCurrentTime(video.currentTime); };
    const handleSeeked = () => setCurrentTime(video.currentTime);
    const handleMeta = () => setDuration(video.duration || 0);
    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('seeked', handleSeeked);
    video.addEventListener('loadedmetadata', handleMeta);
    if (video.readyState >= 1) handleMeta();
    if (!video.paused) handlePlay();
    return () => {
      cancelAnimationFrame(raf);
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('seeked', handleSeeked);
      video.removeEventListener('loadedmetadata', handleMeta);
    };
  }, [videoUrl]);

  // Poll while video hasn't landed yet, or its processing job is still running.
  useEffect(() => {
    if (!workspace) return;
    const needsPoll = !workspace.videoId || (workspace.latestJob && !isTerminalJobStatus(workspace.latestJob.status));
    if (needsPoll) {
      pollRef.current = setInterval(() => fetchWorkspace(workspace.id), 2000);
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [workspace, fetchWorkspace]);

  // Once the video's processing job finishes, load its transcript into the caption list.
  useEffect(() => {
    if (!workspace || !workspace.videoId || !projectId || captionsLoaded) return;
    if (!workspace.latestJob || workspace.latestJob.status !== 'done' && workspace.latestJob.status !== 'completed') return;

    fetch(`${API_BASE}/api/projects/${projectId}/videos/${workspace.videoId}`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        console.log('[KineticCaption] video detail response videoUrl:', data?.videoUrl);
        if (data?.videoUrl) setVideoUrl(data.videoUrl);
        const segments = data?.transcript?.segments;
        if (Array.isArray(segments) && segments.length > 0) {
          setCaptions(segments.map((seg: any, i: number) => ({
            id: `seg-${i}`,
            text: seg.text || '',
            start_time: seg.start ?? 0,
            end_time: seg.end ?? 0,
            style: {},
            words: Array.isArray(seg.words)
              ? seg.words.map((w: any) => ({ text: w.word ?? w.text ?? '', start: w.start ?? 0, end: w.end ?? 0 }))
              : [],
          })));
        }
        setCaptionsLoaded(true);
      })
      .catch(() => setCaptionsLoaded(true));
  }, [workspace, projectId, captionsLoaded]);

  // Debug: trace whether kc-video-panel has what it needs to show the uploaded video.
  useEffect(() => {
    console.log('[KineticCaption] video panel state:', {
      workspaceId: workspace?.id,
      videoId: workspace?.videoId,
      captionsLoaded,
      videoUrl,
      willRenderVideoEl: !!(workspace?.videoId && captionsLoaded && videoUrl),
    });
  }, [workspace, captionsLoaded, videoUrl]);

  // Restore the workspace's last-used style once, on initial load — don't stomp live edits on later polls.
  useEffect(() => {
    if (!workspace?.kineticCaptionConfig || appliedInitialConfigRef.current) return;
    appliedInitialConfigRef.current = true;
    applyConfig({ ...buildConfig(), ...workspace.kineticCaptionConfig });
  }, [workspace]);

  // Auto-preview the render that was just requested, once it finishes.
  useEffect(() => {
    if (!pendingNewJobId) return;
    const job = jobs.find(j => j.id === pendingNewJobId);
    if (!job) return;
    if (isTerminalJobStatus(job.status)) {
      if (job.status !== 'failed' && job.outputUrl) setPreviewJob(job);
      setPendingNewJobId(null);
    }
  }, [jobs, pendingNewJobId]);

  useEffect(() => {
    if (editingName) {
      setDraftName(workspace?.name || 'Untitled');
      requestAnimationFrame(() => nameInputRef.current?.focus());
    }
  }, [editingName, workspace]);

  const saveName = async () => {
    const trimmed = draftName.trim();
    if (!trimmed || !projectId) return;
    setSavingName(true);
    setError(null);
    try {
      const res = workspace
        ? await fetch(`${API_BASE}/api/caption-workspaces/${workspace.id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: trimmed }),
          })
        : await fetch(`${API_BASE}/api/projects/${projectId}/caption-workspaces`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ name: trimmed }),
          });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to save caption workspace name');
      }
      const data = await res.json();
      if (workspace) {
        setWorkspace({ ...workspace, name: data.name });
      } else {
        setWorkspace({ id: data.id, name: data.name, status: data.status, videoId: data.videoId, latestJob: null, kineticCaptionConfig: null });
        navigate(`/projects/${projectId}/kinetic-caption/${data.id}`, { replace: true });
      }
      setEditingName(false);
    } catch (e: any) {
      setError(e.message || 'Failed to save name');
    } finally {
      setSavingName(false);
    }
  };

  const pickPreset = (presetId: string) => {
    setSelectedPreset(presetId);
    setCustomizeOpen(true);
  };

  const handleRegenerateCaptions = () => {
    setShowRetranscribeModal(true);
  };

  const doRetranscribe = async (chargeAuthorizationId?: string) => {
    if (!workspace) return;
    setRetranscribing(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/caption-workspaces/${workspace.id}/retranscribe`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...(chargeAuthorizationId ? { chargeAuthorizationId } : {}) }),
      });
      if (res.status === 402) {
        const payload = await res.json().catch(() => null);
        if (isPaymentRequiredPayload(payload)) {
          setPaymentWall({ preflight: payload, action: 'retranscribe' });
          return;
        }
        throw new Error('Usage limit reached. Open billing to continue.');
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start caption regeneration');
      }
      setShowRetranscribeModal(false);
      setCaptionsLoaded(false); // let the transcript-loading effect pick up the fresh transcript once this job completes
      fetchWorkspace(workspace.id);
    } catch (e: any) {
      setError(e.message || 'Failed to start caption regeneration');
    } finally {
      setRetranscribing(false);
    }
  };

  const buildConfig = (): KineticCaptionConfig => ({
    preset: selectedPreset, fontFamily, fontSizePct, bold, italic, underline, textColor, highlightColor, aspectRatio,
  });

  const applyConfig = (config: KineticCaptionConfig) => {
    setSelectedPreset(config.preset);
    setFontFamily(config.fontFamily);
    setFontSizePct(config.fontSizePct ?? legacyPxToPct(config.fontSize));
    setBold(config.bold);
    setItalic(config.italic);
    setUnderline(config.underline);
    setTextColor(config.textColor);
    setHighlightColor(config.highlightColor);
    setAspectRatio(config.aspectRatio || '9:16');
  };

  const findDuplicateJob = (config: KineticCaptionConfig) => {
    const serialized = JSON.stringify(config);
    return jobs.find(j => j.status !== 'failed' && j.kineticCaption && JSON.stringify(j.kineticCaption) === serialized);
  };

  const doRender = async (config: KineticCaptionConfig, chargeAuthorizationId?: string) => {
    if (!workspace) return;
    setGenerating(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/api/caption-workspaces/${workspace.id}/render`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          kineticCaption: config,
          captions: captions.map(c => ({
            text: c.text, start: c.start_time, end: c.end_time,
            ...(c.words && c.words.length > 0 ? { words: c.words } : {}),
          })),
          ...(chargeAuthorizationId ? { chargeAuthorizationId } : {}),
        }),
      });
      if (res.status === 402) {
        const payload = await res.json().catch(() => null);
        if (isPaymentRequiredPayload(payload)) {
          setPaymentWall({ preflight: payload, action: 'render', config });
          return;
        }
        throw new Error('Usage limit reached. Open billing to continue.');
      }
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to start caption generation');
      }
      const data = await res.json().catch(() => ({}));
      if (data?.jobId) setPendingNewJobId(data.jobId);
      setShowDuplicateModal(false);
      setPendingConfig(null);
      setTab('renders');
      setRefreshSignal(s => s + 1);
    } catch (e: any) {
      setError(e.message || 'Failed to start caption generation');
    } finally {
      setGenerating(false);
    }
  };

  // Caption at the playhead, or the last one passed, so the phone "now playing" strip is never blank.
  const nowCaption = useMemo(() => {
    let last: Caption | null = null;
    for (const c of captions) {
      if (currentTime >= c.start_time && currentTime < c.end_time) return c;
      if (c.start_time <= currentTime) last = c;
    }
    return last;
  }, [captions, currentTime]);

  useEffect(() => {
    if (!styleOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setStyleOpen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [styleOpen]);

  const handleGenerate = () => {
    const config = buildConfig();
    if (findDuplicateJob(config)) {
      setPendingConfig(config);
      setShowDuplicateModal(true);
      return;
    }
    doRender(config);
  };

  // Which finished video "Generate Shorts" was invoked on. An edited version is the export's own
  // job, and its timeline no longer matches the transcript — which is what the modal defaults on.
  const handleGenerateShorts = (job: RenderJob, output?: RenderOutput, edit?: RenderEdit | null) => {
    const clipName = output?.title || workspace?.name || 'Caption';
    setShortsTarget(
      edit
        ? { jobId: edit.id, defaultName: `${clipName} (edited) — Shorts`, timelinePreserved: false }
        : { jobId: job.id, defaultName: `${workspace?.name || clipName} — Shorts`, timelinePreserved: true }
    );
  };

  const openDerivedShorts = (derived: DerivedShorts) => {
    navigate(`/projects/${derived.projectId || projectId}/item/shorts/${derived.videoId}`);
  };

  const handleRegenerateFromRow = (config: Record<string, any>) => {
    const typedConfig = config as KineticCaptionConfig;
    applyConfig(typedConfig);
    setTab('style');
    setPreviewJob(null);
    setPendingConfig(typedConfig);
    setShowDuplicateModal(true);
  };

  const closePaymentWall = () => {
    if (processingPaymentWall) return;
    pendingChargeAuthorizationIdRef.current = null;
    setPaymentWall(null);
  };

  const handleShowPlans = () => setError(null);

  const handlePayNowAuthorization = async (): Promise<PaymentWallConfirmation> => {
    if (!paymentWall || !projectId || !workspace) {
      throw new Error('Payment wall context is unavailable.');
    }
    const wall = paymentWall;
    setProcessingPaymentWall(true);
    setError(null);
    try {
      const methodsRes = await fetch(`${API_BASE}/api/me/billing/payment-methods`, { credentials: 'include' });
      if (methodsRes.ok) {
        const methodsData = await methodsRes.json().catch(() => null) as { paymentMethods?: unknown[] } | null;
        const paymentMethods: unknown[] = Array.isArray(methodsData?.paymentMethods) ? (methodsData?.paymentMethods as unknown[]) : [];
        if (paymentMethods.length === 0) {
          window.location.assign('/account/billing');
          throw new Error('No payment method found. Add a payment method to continue.');
        }
      }

      const payment = await runPayNowFlow({
        featureKey: wall.preflight.featureKey,
        units: wall.preflight.units,
        context: { projectId, videoId: workspace.videoId || '' },
        pricingSnapshot: {
          unitPrice: wall.preflight.paygOption.unitPrice,
          currency: wall.preflight.paygOption.currency,
        },
        fallbackAmount: wall.preflight.paygOption.estimatedAmount,
        fallbackCurrency: wall.preflight.paygOption.currency,
      });

      pendingChargeAuthorizationIdRef.current = payment.chargeAuthorizationId;
      return { outcome: 'payment_authorized', amount: payment.amount, currency: payment.currency };
    } catch (err: any) {
      if (typeof err?.message === 'string' && /payment method/i.test(err.message)) {
        window.location.assign('/account/billing');
      }
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  const handleResumeBlockedAction = async (): Promise<void> => {
    if (!paymentWall) return;
    const wall = paymentWall;
    setProcessingPaymentWall(true);
    setError(null);
    try {
      const chargeAuthorizationId = pendingChargeAuthorizationIdRef.current || undefined;
      if (wall.action === 'retranscribe') {
        await doRetranscribe(chargeAuthorizationId);
      } else {
        await doRender(wall.config!, chargeAuthorizationId);
      }
      pendingChargeAuthorizationIdRef.current = null;
      setPaymentWall(null);
    } catch (err: any) {
      setError(err.message);
      throw err;
    } finally {
      setProcessingPaymentWall(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="clip-editor-loading">Loading...</div>
        </main>
      </div>
    );
  }

  if (paymentWall) {
    return (
      <div className="dashboard-layout">
        <Sidebar />
        <main className="main-content">
          <Header />
          <div className="breadcrumb">
            <BreadcrumbBack to="/projects" />
            <Link to="/projects">Projects</Link>
            <span className="breadcrumb-sep">&#8250;</span>
            <span>{project?.name}</span>
          </div>
          {error && <div className="error-banner">&#9888;&#65039; {error}</div>}
          <FullPagePaymentWall
            preflight={paymentWall.preflight}
            busy={processingPaymentWall}
            onPayNow={handlePayNowAuthorization}
            onShowPlans={handleShowPlans}
            onResumeBlockedAction={handleResumeBlockedAction}
            onCancel={closePaymentWall}
          />
        </main>
      </div>
    );
  }

  const selectedPresetDef = PRESETS.find(p => p.id === selectedPreset);
  const unlocked = !!workspace;
  const showWorkspaceForm = unlocked && workspace!.videoId && captionsLoaded;

  // Size is a % of frame height, so the preview needs no canvas-specific scaling —
  // the same percentage against the preview box matches what ffmpeg burns.
  const videoBox = fitFrameSize(aspectRatio, frameBounds);
  const previewFontSize = (videoBox.height * fontSizePct) / 100;

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="main-content">
        <Header />

        <div className="breadcrumb">
          <BreadcrumbBack to={`/projects/${projectId}`} />
          <Link to="/projects">Projects</Link>
          <span className="breadcrumb-sep">&#8250;</span>
          <Link to={`/projects/${projectId}`}>{project?.name}</Link>
          <span className="breadcrumb-sep">&#8250;</span>
          {editingName ? (
            <span className="kc-name-edit">
              <input
                ref={nameInputRef}
                value={draftName}
                onChange={e => setDraftName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveName(); }}
                maxLength={80}
                disabled={savingName}
              />
              <button className="header-action-btn" onClick={saveName} disabled={!draftName.trim() || savingName}>
                {savingName ? (workspace ? 'Updating…' : 'Saving…') : (workspace ? 'Update' : 'Save')}
              </button>
            </span>
          ) : (
            <span onClick={() => setEditingName(true)} style={{ cursor: 'pointer' }}>{workspace?.name}</span>
          )}
        </div>

        {/* Phones: the style column lives in a right-hand drawer, opened from here. */}
        <div className="kc-mobile-bar">
          <span className="kc-mobile-label">&#127916; Captions</span>
          <button
            type="button"
            className="kc-style-btn"
            onClick={() => setStyleOpen(true)}
            title="Caption styles"
            aria-label="Caption styles"
            aria-expanded={styleOpen}
          >
            <StyleIcon aria-hidden={true} />
          </button>
        </div>

        {error && <div className="error-banner">&#9888;&#65039; {error}</div>}

        <div className={`kc-workspace ${unlocked ? '' : 'kc-workspace--locked'}`}>
          <div className="kc-workspace-inner">
            <div className="kc-center-col">
              {!showWorkspaceForm && workspace && (
                <KineticVideoUpload
                  projectId={projectId!}
                  workspaceId={workspace.id}
                  videoId={workspace.videoId}
                  latestJob={workspace.latestJob}
                  onConfirmed={() => fetchWorkspace(workspace.id)}
                />
              )}

              {showWorkspaceForm && (
                <>
                  <div className="kc-sticky-player">
                  <div className="kc-video-panel">
                    {previewJob ? (
                      <div className="kc-video-frame kc-video-frame--player">
                        <div className="kc-preview-header">
                          <span className="kc-preview-title">{presetLabel(previewJob.kineticCaption?.preset)}</span>
                          <button className="kc-preview-close" onClick={() => setPreviewJob(null)} title="Back to style editor">&#10005;</button>
                        </div>
                        <video
                          key={previewJob.id}
                          className="kc-video-player"
                          src={previewJob.outputUrl || undefined}
                          controls
                          autoPlay
                        />
                      </div>
                    ) : (
                      <div className="kc-video-frame" ref={frameRef}>
                        {videoUrl && (
                          <video
                            ref={videoRef}
                            className="kc-video-el"
                            src={videoUrl}
                            style={videoBox}
                            onClick={() => {
                              const v = videoRef.current;
                              if (v) v.paused ? v.play() : v.pause();
                            }}
                          />
                        )}
                        {videoUrl && (
                          <div className="kc-caption-anchor" style={{ ...videoBox, fontSize: previewFontSize }}>
                            <KineticCaptionOverlay
                              captions={captions}
                              currentTime={currentTime}
                              preset={selectedPreset}
                              fontFamily={fontFamily}
                              fontSize={previewFontSize}
                              bold={bold}
                              italic={italic}
                              underline={underline}
                              textColor={textColor}
                              highlightColor={highlightColor}
                            />
                          </div>
                        )}
                        {videoUrl && (
                          <div className="video-controls kc-video-controls">
                            <button
                              className="play-btn"
                              onClick={() => {
                                const v = videoRef.current;
                                if (v) v.paused ? v.play() : v.pause();
                              }}
                            >
                              {playing ? '⏸' : '▶'}
                            </button>
                            <span className="time-display">{formatClock(currentTime)}</span>
                            <input
                              type="range"
                              className="seek-bar"
                              min={0}
                              max={duration || 0}
                              step={0.01}
                              value={Math.min(currentTime, duration || 0)}
                              onChange={e => {
                                const t = parseFloat(e.target.value);
                                if (videoRef.current) videoRef.current.currentTime = t;
                                setCurrentTime(t);
                              }}
                            />
                            <span className="time-display">{formatClock(duration)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Phones: pinned under the video so the caption at the playhead stays in view. */}
                  <div className="kc-now-caption">
                    {nowCaption ? (
                      <>
                        <span className="kc-now-time">{nowCaption.start_time.toFixed(1)}&#8211;{nowCaption.end_time.toFixed(1)}</span>
                        <span className="kc-now-text">{nowCaption.text}</span>
                      </>
                    ) : (
                      <span className="kc-now-empty">No captions yet</span>
                    )}
                  </div>
                  </div>

                  <CaptionPanel
                    captions={captions}
                    onCaptionsChange={setCaptions}
                    onRegenerate={handleRegenerateCaptions}
                    currentTime={currentTime}
                    onSeek={(t) => {
                      if (videoRef.current) videoRef.current.currentTime = t;
                      setCurrentTime(t);
                    }}
                  />
                </>
              )}
            </div>

            <div className={`kc-style-backdrop${styleOpen ? ' is-open' : ''}`} onClick={() => setStyleOpen(false)} aria-hidden="true" />
            <div className={`kc-style-col${styleOpen ? ' kc-style-col--open' : ''}`}>
              <div className="kc-drawer-head">
                <span className="kc-drawer-title">Caption styles</span>
                <button type="button" className="kc-drawer-close" onClick={() => setStyleOpen(false)} aria-label="Close caption styles">&#10005;</button>
              </div>
              <div className="kc-tabs">
                <button className={`kc-tab ${tab === 'style' ? 'kc-tab--active' : ''}`} onClick={() => setTab('style')}>Style</button>
                <button className={`kc-tab ${tab === 'renders' ? 'kc-tab--active' : ''}`} onClick={() => setTab('renders')}>Renders</button>
              </div>

              {tab === 'style' ? (
                <>
                  <div className="kc-preset-grid">
                    {PRESETS.map(preset => (
                      <button
                        key={preset.id}
                        className={`kc-preset-card ${selectedPreset === preset.id ? 'kc-preset-card--active' : ''}`}
                        onClick={() => pickPreset(preset.id)}
                      >
                        <span className="kc-preset-thumb">
                          <KineticPresetPreview presetId={preset.id} />
                        </span>
                        <span className="kc-preset-label">{preset.label}</span>
                        {preset.premium && <span className="kc-preset-lock" title="Premium preset">&#128274;</span>}
                      </button>
                    ))}
                  </div>

                  <div className="kc-customize">
                    <button className="kc-customize-toggle" onClick={() => setCustomizeOpen(v => !v)}>
                      <span>Customize</span>
                      <span className={`kc-chevron ${customizeOpen ? 'kc-chevron--open' : ''}`}>&#8250;</span>
                    </button>
                    {customizeOpen && (
                      <div className="se-text-style-toolbar">
                        <div className="se-select-wrap se-style-font-wrap">
                          <select className="se-style-select" value={fontFamily} onChange={e => setFontFamily(e.target.value)}>
                            {CAPTION_FONTS.map(f => <option key={f} value={f}>{f}</option>)}
                          </select>
                        </div>
                        <span className="se-style-divider" />
                        <div className="se-size-stepper">
                          <button type="button" title="Smaller captions" onClick={() => setFontSizePct(v => Math.max(FONT_PCT_MIN, +(v - FONT_PCT_STEP).toFixed(1)))}>&#8722;</button>
                          <input
                            type="number"
                            value={fontSizePct}
                            min={FONT_PCT_MIN}
                            max={FONT_PCT_MAX}
                            step={FONT_PCT_STEP}
                            title="Caption size, as a percentage of video height"
                            onChange={e => {
                              const v = Number(e.target.value);
                              if (!isNaN(v)) setFontSizePct(Math.min(FONT_PCT_MAX, Math.max(FONT_PCT_MIN, v)));
                            }}
                          />
                          <span className="kc-unit">%</span>
                          <button type="button" title="Larger captions" onClick={() => setFontSizePct(v => Math.min(FONT_PCT_MAX, +(v + FONT_PCT_STEP).toFixed(1)))}>+</button>
                        </div>
                        <span className="se-style-divider" />
                        <button type="button" className={`se-style-icon-btn ${bold ? 'se-format-active' : ''}`} onClick={() => setBold(v => !v)}><b>B</b></button>
                        <button type="button" className={`se-style-icon-btn ${italic ? 'se-format-active' : ''}`} onClick={() => setItalic(v => !v)}><i>I</i></button>
                        <button type="button" className={`se-style-icon-btn ${underline ? 'se-format-active' : ''}`} onClick={() => setUnderline(v => !v)}><u>U</u></button>
                        <span className="se-style-divider" />
                        <label className="se-style-icon-btn se-color-swatch" title="Text color">
                          <span className="se-color-swatch-bar" style={{ background: textColor }} />
                          <input type="color" value={textColor} onChange={e => setTextColor(e.target.value)} />
                        </label>
                        <label className="se-style-icon-btn se-color-swatch" title="Highlight color">
                          <span className="se-color-swatch-fill" style={{ background: highlightColor }} />
                          <input type="color" value={highlightColor} onChange={e => setHighlightColor(e.target.value)} />
                        </label>
                      </div>
                    )}
                  </div>

                  <div className="se-field">
                    <label className="se-label">Aspect Ratio</label>
                    <div className="se-aspect-ratio-grid">
                      {ASPECT_RATIOS.map(ar => (
                        <button
                          key={ar.value}
                          type="button"
                          className={`se-aspect-btn ${aspectRatio === ar.value ? 'se-aspect-active' : ''}`}
                          onClick={() => setAspectRatio(ar.value)}
                          title={ar.label}
                        >
                          <span className="se-aspect-label">{ar.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="kc-cta">
                    <button className="btn-primary btn-full" onClick={() => { setStyleOpen(false); handleGenerate(); }} disabled={generating || !showWorkspaceForm}>
                      {generating ? 'Generating…' : 'Generate Kinetic Captions'}
                    </button>
                    {selectedPresetDef?.premium && (
                      <div className="kc-cost-row">
                        <span className="fpw-pill fpw-pill-muted">&#8776; 1 credit &middot; $0.40</span>
                      </div>
                    )}
                    <p className="kc-cta-note">Uses your kinetic caption quota, or pay-per-render if exhausted.</p>
                  </div>
                </>
              ) : (
                workspace && (
                  <RendersPanel
                    workspaceId={workspace.id}
                    onJobsChange={setJobs}
                    onRegenerate={handleRegenerateFromRow}
                    onGenerateShorts={handleGenerateShorts}
                    onOpenDerivedShorts={openDerivedShorts}
                    onView={setPreviewJob}
                    selectedJobId={previewJob?.id}
                    refreshSignal={refreshSignal}
                  />
                )
              )}
            </div>
          </div>

          {!unlocked && (
            <div className="kc-lock-overlay">
              <div className="kc-lock-card">
                <div className="kc-lock-icon">&#128274;</div>
                <h3>Name your caption project to continue</h3>
                <p>Save a name in the breadcrumb above to unlock the caption workspace.</p>
              </div>
            </div>
          )}
        </div>

        {showDuplicateModal && pendingConfig && (
          <RenderConfirmModal
            busy={generating}
            onCancel={() => { setShowDuplicateModal(false); setPendingConfig(null); }}
            onConfirm={() => doRender(pendingConfig)}
          />
        )}

        {showRetranscribeModal && (
          <RenderConfirmModal
            busy={retranscribing}
            title="Regenerate captions?"
            message="This re-runs transcription on your video and replaces the current caption text and timing. Any edits you've made will be lost."
            confirmLabel="Regenerate"
            busyLabel="Regenerating…"
            costSuffix="per regenerate"
            onCancel={() => setShowRetranscribeModal(false)}
            onConfirm={() => doRetranscribe()}
          />
        )}

        {workspace && (
          <GenerateShortsModal
            open={!!shortsTarget}
            target={shortsTarget}
            projectId={projectId!}
            workspaceId={workspace.id}
            onClose={() => setShortsTarget(null)}
            onGenerated={({ videoId }) => {
              setShortsTarget(null);
              // The clips are what the author asked for, so land on the item that will hold
              // them rather than leaving them to find it in the project list.
              navigate(`/projects/${projectId}/item/shorts/${videoId}`);
            }}
          />
        )}
      </main>
    </div>
  );
};

export default KineticCaption;
